'use client';

import { useRouter } from 'next/navigation';
import React, { useState, useEffect, useCallback } from 'react';
import { generateRoomId } from '@/lib/client-utils';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import { useAuth } from '@/lib/AuthContext';
import { meetingsApi } from '@/lib/api';
import { Modal } from '@/components/Modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from '@/styles/MeetingEntry.module.css';
import { formatInKathmanduTime } from '@/lib/timezone';

interface ScheduledMeeting {
    id: string;
    roomName: string;
    title: string;
    description?: string | null;
    scheduledAt: string;
    createdBy: string;
    isActive: boolean;
}

export function MeetingEntry() {
    const router = useRouter();
    const { user, token } = useAuth();
    const [scheduledMeetings, setScheduledMeetings] = useState<ScheduledMeeting[]>([]);
    const [meetingCode, setMeetingCode] = useState('');

    // Modal & Form State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [meetingTitle, setMeetingTitle] = useState('');
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [isScheduling, setIsScheduling] = useState(false);

    // Instant meeting modal
    const [isInstantModalOpen, setIsInstantModalOpen] = useState(false);
    const [instantTitle, setInstantTitle] = useState('');

    // Fetch meetings from Backend
    const fetchMeetings = useCallback(async () => {
        if (!user || !token) return;
        try {
            const data = await meetingsApi.list(token, user.id);
            setScheduledMeetings(data);
        } catch (error) {
            console.error('Error fetching meetings:', error);
        }
    }, [user, token]);

    useEffect(() => {
        if (!user || !token) {
            setScheduledMeetings([]);
            return;
        }
        fetchMeetings();
        // Fallback polling for updates
        const interval = setInterval(fetchMeetings, 10000);
        return () => clearInterval(interval);
    }, [user, token, fetchMeetings]);

    const startNewMeeting = async () => {
        if (!user || !token) {
            Swal.fire({ icon: 'error', title: 'Authentication Required', text: 'You must be signed in to start a meeting.' });
            return;
        }
        setIsInstantModalOpen(true);
    };

    const handleStartInstantMeeting = async () => {
        if (!user || !token) return;
        if (!instantTitle.trim()) {
            Swal.fire({ icon: 'error', title: 'Title Required', text: 'Please enter a meeting title.' });
            return;
        }
        try {
            const meeting = await meetingsApi.createInstant(token, user.id, instantTitle.trim());
            setIsInstantModalOpen(false);
            setInstantTitle('');
            router.push(`/rooms/${meeting.roomName}`);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Failed to Start Meeting', text: 'An error occurred while creating the meeting.' });
        }
    };

    const handleJoinByCode = () => {
        if (!meetingCode.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Code',
                text: 'Please enter a meeting code.',
            });
            return;
        }

        const code = meetingCode.trim();
        // Validate meeting code format (should be like "smarten-xxxx-xxxx")
        if (!code.startsWith('smarten-')) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Code',
                text: 'Meeting code should start with "smarten-".',
            });
            return;
        }

        const url = `/rooms/${code}`;
        router.push(url);
    };

    const handleScheduleMeeting = async () => {
        if (!user || !token) {
            Swal.fire({
                icon: 'error',
                title: 'Authentication Required',
                text: 'You must be signed in to schedule a meeting.',
            });
            return;
        }

        if (!meetingTitle || !selectedDate || isNaN(selectedDate.getTime())) {
            toast.error('Please enter a title and select a valid date');
            return;
        }

        setIsScheduling(true);

        try {
            await meetingsApi.schedule(token, {
                title: meetingTitle,
                scheduledAt: selectedDate.toISOString(),
                createdBy: user.id,
            });

            setIsModalOpen(false);
            setMeetingTitle('');
            setSelectedDate(new Date());
            await fetchMeetings();

            Swal.fire({
                icon: 'success',
                title: 'Meeting Scheduled!',
                text: `"${meetingTitle}" has been saved.`,
                timer: 1500,
                showConfirmButton: false
            });
        } catch (e: any) {
            console.error('Error scheduling:', e);
            Swal.fire({
                icon: 'error',
                title: 'Scheduling Failed',
                text: e.message || 'Failed to save meeting to database.',
            });
        } finally {
            setIsScheduling(false);
        }
    };

    const handleActivateMeeting = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user || !token) return;

        try {
            await meetingsApi.start(token, id, user.id);
            toast.success('Meeting started!');
            await fetchMeetings();
        } catch (error) {
            toast.error('Failed to start meeting');
        }
    };

    const deleteMeeting = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        Swal.fire({
            title: 'Delete scheduling?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#333',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (!user || !token) return;
                    await meetingsApi.delete(token, id, user.id);
                    await fetchMeetings();
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Meeting has been removed.',
                        timer: 1000,
                        showConfirmButton: false
                    });
                } catch (e) {
                    console.error('Error deleting:', e);
                }
            }
        });
    };

    const joinMeeting = (meeting: ScheduledMeeting) => {
        if (!meeting.isActive && meeting.createdBy === user?.id) {
            Swal.fire({
                title: 'Meeting Not Started',
                text: 'Would you like to start this meeting now?',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Start & Join',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        if (!user || !token) return;
                        await meetingsApi.start(token, meeting.id, user.id);
                        const url = `/rooms/${meeting.roomName}`;
                        router.push(url);
                    } catch (error) {
                        toast.error('An error occurred');
                    }
                }
            });
            return;
        }

        if (!meeting.isActive) {
            toast.error('This meeting has not started yet.');
            return;
        }

        const url = `/rooms/${meeting.roomName}`;
        router.push(url);
    };

    const formatMeetingDate = (dateStr: string) => {
        return formatInKathmanduTime(dateStr, 'MMM d, yyyy, hh:mm a');
    };

    return (
        <div className={styles['meeting-entry-wrapper']}>
            <div className={styles['meeting-entry-container']}>
                {/* Start Meeting & Schedule Meeting Row */}
                <div className={styles['action-buttons-row']}>
                    {/* Start Meeting */}
                    <button className={styles['primary-btn']} onClick={startNewMeeting}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M23 7l-7 5 7 5V7z"/>
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                        </svg>
                        Start Instant Meeting
                    </button>

                    {/* Schedule Meeting */}
                    <button className={styles['secondary-btn']} onClick={() => setIsModalOpen(true)}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        Schedule Meeting
                    </button>
                </div>

                {/* Join by Meeting Code - Below the buttons row */}
                <div className={styles['join-by-code-container']}>
                    <input
                        type="text"
                        placeholder="Enter meeting code (e.g., smarten-xxxx-xxxx)"
                        value={meetingCode}
                        onChange={(e) => setMeetingCode(e.target.value)}
                        className={styles['meeting-code-input']}
                        onKeyDown={(e) => e.key === 'Enter' && handleJoinByCode()}
                    />
                    <button className={styles['join-btn']} onClick={handleJoinByCode}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                        </svg>
                        Join
                    </button>
                </div>
            </div>
            <Modal
                isOpen={isInstantModalOpen}
                onClose={() => { setIsInstantModalOpen(false); setInstantTitle(''); }}
                title="Start Instant Meeting"
            >
                <div className={styles['schedule-form']}>
                    <div className={styles['form-group']}>
                        <label>Meeting Title</label>
                        <input
                            type="text"
                            placeholder="e.g. Quick Sync (optional)"
                            value={instantTitle}
                            onChange={(e) => setInstantTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleStartInstantMeeting()}
                            autoFocus
                        />
                    </div>
                    <button className={styles['submit-btn']} onClick={handleStartInstantMeeting}>
                        Start Meeting
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Schedule Meeting"
            >
                <div className={styles['schedule-form']}>
                    <div className={styles['form-group']}>
                        <label>Meeting Title</label>
                        <input
                            type="text"
                            placeholder="e.g. Weekly Sync"
                            value={meetingTitle}
                            onChange={(e) => setMeetingTitle(e.target.value)}
                        />
                    </div>
                    <div className={styles['form-group']}>
                        <label>Date & Time</label>
                        <DatePicker
                            selected={selectedDate}
                            onChange={(date: Date | null) => setSelectedDate(date)}
                            showTimeSelect
                            dateFormat="MMMM d, yyyy h:mm aa"
                            className="date-picker-input"
                            placeholderText="Select date and time"
                            minDate={new Date()}
                        />
                    </div>
                    <button
                        className={styles['submit-btn']}
                        onClick={handleScheduleMeeting}
                        disabled={isScheduling}
                    >
                        {isScheduling ? 'Scheduling...' : 'Confirm Schedule'}
                    </button>
                </div>
            </Modal>
        </div>
    );
}
